/**
 * Connected Services Store
 * 
 * This module provides a Zustand store for managing connected services state across the application.
 * It centralizes the state management for connected services, databases, and related operations.
 * 
 * @module store/useConnectedServicesStore
 */

import { create } from 'zustand';

/**
 * Connected service interface
 */
interface ConnectedService {
  id: number;
  name: string;
  baseUrl: string;
  headers: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Connected services state interface
 * 
 * @interface ConnectedServicesState
 */
interface ConnectedServicesState {
  // State properties
  services: ConnectedService[];
  selectedService: ConnectedService | null;
  databaseOptions: string[];
  isLoading: boolean;
  error: string | null;
  loadingDatabases: boolean;
  databaseError: string | null;
  
  // Actions
  setServices: (services: ConnectedService[]) => void;
  setSelectedService: (service: ConnectedService | null) => void;
  setDatabaseOptions: (options: string[]) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setLoadingDatabases: (loading: boolean) => void;
  setDatabaseError: (error: string | null) => void;
  reset: () => void;
  
  // Async actions
  fetchDatabases: (serviceId: number) => Promise<void>;
}

/**
 * Connected services store using Zustand
 * 
 * This store manages the connected services state and provides actions for manipulating it.
 * 
 * @example
 * // Using the connected services store in a component
 * const { 
 *   services, 
 *   selectedService, 
 *   databaseOptions,
 *   setSelectedService 
 * } = useConnectedServicesStore();
 * 
 * // To select a service
 * setSelectedService(services.find(s => s.id === serviceId));
 */
export const useConnectedServicesStore = create<ConnectedServicesState>()((set, get) => ({
  // Initial state
  services: [],
  selectedService: null,
  databaseOptions: [],
  isLoading: false,
  error: null,
  loadingDatabases: false,
  databaseError: null,
  
  // Actions
  /**
   * Set the connected services
   * @param {ConnectedService[]} services - The connected services to set
   */
  setServices: (services) => set({ services }),
  
  /**
   * Set the selected service
   * @param {ConnectedService | null} service - The selected service or null
   */
  setSelectedService: (service) => set({ selectedService: service }),
  
  /**
   * Set the database options
   * @param {string[]} options - The database options to set
   */
  setDatabaseOptions: (options) => set({ databaseOptions: options }),
  
  /**
   * Set the loading state
   * @param {boolean} loading - Whether services are loading
   */
  setIsLoading: (loading) => set({ isLoading: loading }),
  
  /**
   * Set the error state
   * @param {string | null} error - The error message or null
   */
  setError: (error) => set({ error }),
  
  /**
   * Set the loading state for databases
   * @param {boolean} loading - Whether databases are loading
   */
  setLoadingDatabases: (loading) => set({ loadingDatabases: loading }),
  
  /**
   * Set the database error
   * @param {string | null} error - The error message or null
   */
  setDatabaseError: (error) => set({ databaseError: error }),
  
  /**
   * Reset the store state
   */
  reset: () => set({
    selectedService: null,
    databaseOptions: [],
    loadingDatabases: false,
    databaseError: null
  }),
  
  /**
   * Fetch databases for a service
   * @param {number} serviceId - The ID of the service to fetch databases for
   */
  fetchDatabases: async (serviceId) => {
    const { services } = get();
    const service = services.find(s => s.id === serviceId);
    
    if (!service) {
      set({ databaseError: "Service not found" });
      return;
    }
    
    set({ 
      selectedService: service,
      loadingDatabases: true,
      databaseError: null
    });
    
    try {
      try {
        console.log('Attempting to fetch databases from real API...');
        // Parse headers if it's a string
        let headersObj = {};
        try {
          if (service.headers) {
            headersObj = typeof service.headers === "string"
              ? JSON.parse(service.headers)
              : service.headers;
          }
        } catch (e) {
          console.error("Error parsing headers:", e);
        }
        
        // Make API call to fetch databases using the baseUrl
        const response = await fetch(`${service.baseUrl}/api/databases`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...headersObj,
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch databases: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Set database options
        set({ 
          databaseOptions: Array.isArray(data) ? data : [],
          loadingDatabases: false
        });
      } catch (apiError) {
        // If real API fails, use mock implementation
        console.warn('Real API call failed, using mock implementation:', apiError);
        console.info('MOCK: Returning mock databases for service:', service.name);
        
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 600));
        
        // Return mock data based on service ID
        let mockDatabases: string[] = [];
        
        switch (service.id) {
          case 1: // Development Instance
            mockDatabases = ['dev_db1', 'dev_db2', 'dev_test_db'];
            break;
          case 2: // Staging Instance
            mockDatabases = ['staging_db1', 'staging_db2', 'staging_test_db'];
            break;
          case 3: // Production Instance
            mockDatabases = ['prod_db1', 'prod_db2', 'prod_main_db', 'prod_archive_db'];
            break;
          default:
            mockDatabases = ['default_db1', 'default_db2'];
        }
        
        console.info('MOCK: Returned mock databases:', mockDatabases);
        
        // Set mock database options
        set({ 
          databaseOptions: mockDatabases,
          loadingDatabases: false
        });
      }
    } catch (error) {
      console.error("Error in fetchDatabases:", error);
      set({
        databaseError: error instanceof Error ? error.message : "Unknown error",
        databaseOptions: [],
        loadingDatabases: false
      });
    }
  }
}));
